import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import dataService from '../services/dataService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreloadingData, setIsPreloadingData] = useState(false);

  const validateToken = (token) => {
    if (!token) return false;
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      const tokenCreationTime = decoded.time;
      const oneDayInSeconds = 24 * 60 * 60;
      
      return currentTime <= tokenCreationTime + oneDayInSeconds;
    } catch (error) {
      return false;
    }
  };

  const checkAuth = () => {
    const token = Cookies.get(process.env.REACT_APP_AUTH_KEY);
    const isValid = validateToken(token);
    
    if (isValid) {
      setIsAuthenticated(true);
      
      try {
        const decoded = jwtDecode(token);
        setUserInfo(decoded);
      } catch (error) {
        setUserInfo(null);
      }
    } else {
      setIsAuthenticated(false);
      setUserInfo(null);
      
      if (token) {
        Cookies.remove(process.env.REACT_APP_AUTH_KEY);
      }
    }
    
    setIsLoading(false);
  };

  const login = async (token) => {
    Cookies.set(process.env.REACT_APP_AUTH_KEY, token);
    checkAuth();
    
    
    setIsPreloadingData(true);
    
    
    try {
      const preloadResult = await dataService.preloadData();
    } catch (error) {
      console.error('❌ Error preloading data:', error);
      
    } finally {
      
      setIsPreloadingData(false);
    }
  };

  const logout = () => {
    Cookies.remove(process.env.REACT_APP_AUTH_KEY);
    setIsAuthenticated(false);
    setUserInfo(null);
    
    dataService.clearCache();
    
    window.location.href = '/login';
  };

  
  useEffect(() => {
    const handleLogoutEvent = () => {
      logout();
    };
    window.addEventListener('logout', handleLogoutEvent);

    
    checkAuth();

    return () => {
      window.removeEventListener('logout', handleLogoutEvent);
    };
  }, []);

  
  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated) {
        checkAuth();
      }
    }, 60000); 

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const value = {
    isAuthenticated,
    userInfo,
    isLoading,
    isPreloadingData,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
